import { useCallback, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setSections,
  setSelectedSection,
} from "@/store/slices/webbuilderSlice";
import {
  selectSectionsArray,
  selectSelectedSection,
} from "@/store/selectors/webbuilder.selectors";
import { Section } from "@/types/models/page";
import { toast } from "sonner";

interface UndoRedoState {
  sections: Section[];
  selectedSectionId: string | null;
  timestamp: number;
}

// Efficient deep comparison function
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a === "object") {
    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    if (a && b && typeof a === "object" && typeof b === "object") {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (
          !deepEqual(
            (a as Record<string, unknown>)[key],
            (b as Record<string, unknown>)[key],
          )
        )
          return false;
      }

      return true;
    }
  }

  return false;
};

export const useUndoRedo = () => {
  const dispatch = useAppDispatch();
  const sections = useAppSelector(selectSectionsArray) as Section[];
  const selectedSection = useAppSelector(selectSelectedSection) as any;

  // Store the history in a ref to avoid re-renders
  const historyRef = useRef<UndoRedoState[]>([]);
  const currentIndexRef = useRef<number>(-1);
  const maxHistorySize = 50; // Maximum number of states to keep in history

  // Initialize history with current state
  useEffect(() => {
    if (sections.length > 0) {
      const currentState: UndoRedoState = {
        sections: JSON.parse(JSON.stringify(sections)), // Deep clone
        selectedSectionId: selectedSection?.id || null,
        timestamp: Date.now(),
      };

      // Only add to history if it's different from the last state using efficient comparison
      const lastState = historyRef.current[currentIndexRef.current];
      if (
        !lastState ||
        !deepEqual(lastState.sections, currentState.sections) ||
        lastState.selectedSectionId !== currentState.selectedSectionId
      ) {
        // Remove any states after current index (if we're not at the end)
        historyRef.current = historyRef.current.slice(
          0,
          currentIndexRef.current + 1,
        );

        // Add new state
        historyRef.current.push(currentState);
        currentIndexRef.current = historyRef.current.length - 1;

        // Limit history size
        if (historyRef.current.length > maxHistorySize) {
          historyRef.current.shift();
          currentIndexRef.current--;
        }

        console.log("📝 Added state to undo history:", {
          totalStates: historyRef.current.length,
          currentIndex: currentIndexRef.current,
          sectionsCount: currentState.sections.length,
        });
      }
    }
  }, [sections, selectedSection]);

  const canUndo = useCallback(() => {
    return currentIndexRef.current > 0;
  }, []);

  const canRedo = useCallback(() => {
    return currentIndexRef.current < historyRef.current.length - 1;
  }, []);

  const undo = useCallback(() => {
    if (!canUndo()) {
      
      toast.error("Nothing to undo");
      return;
    }

    currentIndexRef.current--;
    const previousState = historyRef.current[currentIndexRef.current];

    console.log("↩️ Undoing to state:", {
      index: currentIndexRef.current,
      sectionsCount: previousState.sections.length,
      selectedSectionId: previousState.selectedSectionId,
      sections: previousState.sections.map((s) => ({
        id: s.id,
        componentDataKeys: Object.keys(s.component_data || {}),
        componentData: s.component_data,
      })),
    });

    // Restore the previous state
    dispatch(setSections(previousState.sections));

    // Restore selected section if it exists
    if (previousState.selectedSectionId) {
      const sectionToSelect = previousState.sections.find(
        (s) => s.id === previousState.selectedSectionId,
      );
      if (sectionToSelect) {
        dispatch(setSelectedSection(sectionToSelect));
        console.log("✅ Restored selected section:", sectionToSelect.id);
      }
    } else {
      dispatch(setSelectedSection(null));
      
    }

    toast.success("Undo completed");
  }, [canUndo, dispatch]);

  const redo = useCallback(() => {
    if (!canRedo()) {
      
      toast.error("Nothing to redo");
      return;
    }

    currentIndexRef.current++;
    const nextState = historyRef.current[currentIndexRef.current];

    console.log("↪️ Redoing to state:", {
      index: currentIndexRef.current,
      sectionsCount: nextState.sections.length,
      selectedSectionId: nextState.selectedSectionId,
      sections: nextState.sections.map((s) => ({
        id: s.id,
        componentDataKeys: Object.keys(s.component_data || {}),
        componentData: s.component_data,
      })),
    });

    // Restore the next state
    dispatch(setSections(nextState.sections));

    // Restore selected section if it exists
    if (nextState.selectedSectionId) {
      const sectionToSelect = nextState.sections.find(
        (s) => s.id === nextState.selectedSectionId,
      );
      if (sectionToSelect) {
        dispatch(setSelectedSection(sectionToSelect));
        console.log("✅ Restored selected section:", sectionToSelect.id);
      }
    } else {
      dispatch(setSelectedSection(null));
      
    }

    toast.success("Redo completed");
  }, [canRedo, dispatch]);

  const clearHistory = useCallback(() => {
    historyRef.current = [];
    currentIndexRef.current = -1;
    
    toast.success("History cleared");
  }, []);

  const getHistoryInfo = useCallback(() => {
    return {
      totalStates: historyRef.current.length,
      currentIndex: currentIndexRef.current,
      canUndo: canUndo(),
      canRedo: canRedo(),
      historySize: historyRef.current.length,
    };
  }, [canUndo, canRedo]);

  const getHistorySize = useCallback(() => {
    return historyRef.current.length;
  }, []);

  const exportHistory = useCallback(() => {
    return {
      history: historyRef.current,
      currentIndex: currentIndexRef.current,
      maxHistorySize,
      totalStates: historyRef.current.length,
    };
  }, []);

  const debugHistory = useCallback(() => {
    console.log("🔍 Undo/Redo History Debug:", {
      totalStates: historyRef.current.length,
      currentIndex: currentIndexRef.current,
      canUndo: canUndo(),
      canRedo: canRedo(),
      history: historyRef.current.map((state, index) => ({
        index,
        sectionsCount: state.sections.length,
        selectedSectionId: state.selectedSectionId,
        timestamp: new Date(state.timestamp).toLocaleTimeString(),
      })),
    });
  }, [canUndo, canRedo]);

  return {
    undo,
    redo,
    canUndo: canUndo(),
    canRedo: canRedo(),
    clearHistory,
    getHistoryInfo,
    getHistorySize,
    exportHistory,
    debugHistory,
  };
};
